// Hideo - script editor: the toolbar without UI (drawn by src/components/editor/EditorToolbar.vue). Ported from the
// legacy editor.js (TB_ITEMS, layoutToolbar, TB_DROPS, tbState's icons).
// The tools wrap onto TWO rows (user decision); whatever still doesn't fit goes into the ⋮ panel. On phones the most used
// tools come first: zoom, font and size go last. They move with their separators (the legacy layout moved the tools and
// left their separators behind, so a phone showed two or three separators in a row).
import { t } from '../../i18n'
import { blockIcon, blockName } from './blockKinds'

/** A toolbar tool, or a separator between two groups of tools */
interface TbEntry {
  /** Tool name (EditorToolbar.vue draws it), or 'sep' */
  key: string
  /** Separators: hidden from 1024 px (the one after the outline button, which is hidden there too) */
  lgHidden?: boolean
}
/** The tools in their groups, in the order of the computer toolbar; a separator goes between two groups */
export const TB_GROUPS: readonly (readonly string[])[] = [
  ['toc'], ['undo', 'redo'], ['zoom'], ['style', 'font'], ['minus', 'fsize', 'plus'],
  ['bold', 'italic', 'underline', 'strike', 'tc', 'tcPick', 'hl', 'hlPick', 'link'], ['align', 'spacing'], ['list', 'upper', 'lower', 'clear'], ['comment']
]
/** Tools that go last on phones (matched by name: the legacy code matched them without their translated titles) */
export const TB_LATE = ['zoom', 'font', 'minus', 'fsize', 'plus']
/** Rows of tools before the ⋮ button appears */
export const TB_ROWS = 2

/** The toolbar's entries in order: on a computer the groups as they are; on a phone (< 768 px) the late tools last,
 *  each with the other late tools of its group. Separators between groups (the first one hidden from 1024 px, like the
 *  outline button before it) */
export function tbEntries(phone: boolean): TbEntry[] {
  let groups: string[][] = TB_GROUPS.map(g => [...g])
  if (phone) {
    const early = groups.map(g => g.filter(k => !TB_LATE.includes(k))).filter(g => g.length)
    const late = groups.map(g => g.filter(k => TB_LATE.includes(k))).filter(g => g.length)
    groups = [...early, ...late]
  }
  const out: TbEntry[] = []
  groups.forEach((g, i) => {
    if (i) out.push(i === 1 && groups[0][0] === 'toc' ? { key: 'sep', lgHidden: true } : { key: 'sep' })
    g.forEach(key => out.push({ key }))
  })
  return out
}

/** One entry as the layout sees it: its box (offsetTop, offsetWidth) and whether it is a separator */
export interface TbBox { top: number; width: number; sep: boolean }
/** The rows the entries occupy (tops at least 12 px apart), top to bottom; entries not shown (width 0) are skipped */
export function tbRows(boxes: readonly TbBox[]): number[] {
  const tops: number[] = []
  boxes.forEach(b => { if (!b.width) return; if (!tops.some(x => Math.abs(x - b.top) < 12)) tops.push(b.top) })
  return tops.sort((a, b) => a - b)
}
/** Where the ⋮ panel starts: the first shown entry below the second row, moved back over separators and entries not
 *  shown (no separator at the end of the bar); -1 = everything fits */
export function tbCut(boxes: readonly TbBox[], rows = TB_ROWS): number {
  const r = tbRows(boxes)
  if (r.length <= rows) return -1
  const lim = r[rows - 1] + 12
  let cut = boxes.findIndex(b => b.width && b.top > lim)
  if (cut < 0) return -1
  while (cut > 0 && (!boxes[cut - 1].width || boxes[cut - 1].sep)) cut--
  return cut
}

/** Toolbar drop-downs: alignment and lists ([value, icon, label]; '-' = a line) */
export const TB_DROPS: Readonly<Record<'align' | 'list', readonly (readonly [string, string?, string?])[]>> = {
  align: [['justifyLeft', 'fa-align-left', t('Align left')], ['justifyCenter', 'fa-align-center', t('Centre')], ['justifyRight', 'fa-align-right', t('Align right')], ['justifyFull', 'fa-align-justify', t('Justify')]],
  list: [...(['ul', 'ol', 'todo'] as const).map(k => [k, blockIcon(k), blockName(k)] as const), ['-'], ['outdent', 'fa-outdent', t('Decrease indent')], ['indent', 'fa-indent', t('Increase indent')]]
}
/** Icon of the alignment button: the alignment where the cursor is */
export const alignIcon = (align: string): string => (TB_DROPS.align.find(x => x[0] === align) || TB_DROPS.align[0])[1] as string
/** Icon of the lists button: the list where the cursor is, else the bulleted list */
export const listIcon = (list: string): string => list ? (TB_DROPS.list.find(x => x[0] === list) || TB_DROPS.list[0])[1] as string : 'fa-list-ul'
